import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Map, ZoomIn, ZoomOut, RotateCcw, Download, Info, Waves, Bath, Car, Blocks, Rabbit, ArrowUpRight, PersonStanding, Dog, Bike, Baby, X } from "lucide-react";
import { Link } from "react-router-dom";
import { Reveal } from "../components/Chapter";
import { BOOKING_URL } from "../lib/data";

const AMENITIES = [
  { icon: Info, label: "Accueil" },
  { icon: Waves, label: "Piscine couverte" },
  { icon: Bath, label: "Sanitaires" },
  { icon: Car, label: "Parkings" },
  { icon: Blocks, label: "Aire de jeux" },
  { icon: Rabbit, label: "Mini-ferme" },
];

const ZONES = [
  { x: 17, y: 45, name: "Emplacements 101–121", type: "Mobil-home 2 ch Confort 22,5 m²", note: "Zone rose, à l'ouest du camping, proche des sanitaires." },
  { x: 20, y: 31, name: "Emplacements 201–216", type: "Mobil-homes Confort climatisés (2 et 3 ch)", note: "Zone bleue et mauve, bien ombragée." },
  { x: 25, y: 26, name: "Emplacements 218–222", type: "Mobil-home 3 ch Premium Plus 32 m² climatisé", note: "Zone terracotta, au calme en haut de l'allée." },
  { x: 23, y: 50, name: "Emplacements 207–210", type: "Mobil-home 2 ch Confort", note: "Zone rose, proche de l'allée centrale." },
  { x: 27, y: 56, name: "Emplacements 101–107", type: "FreeCamp Originale", note: "Zone vert foncé, l'esprit tente en mieux." },
  { x: 42, y: 40, name: "Emplacements 313–330", type: "FreeFlowers", note: "Zone menthe : les grandes tentes au cœur du camping." },
  { x: 53, y: 42, name: "Emplacements 331–353", type: "Emplacements nus", note: "Tentes, caravanes et camping-cars, à deux pas de la piscine." },
  { x: 64, y: 33, name: "Emplacements 501–517", type: "Emplacements tente (Tendi)", note: "Zone vert clair, la plus en hauteur du camping." },
  { x: 63, y: 53, name: "Emplacements 354–372", type: "Emplacements nus", note: "Proches de l'accueil et du parking est." },
  { x: 72, y: 76, name: "Emplacements 401–445", type: "Lodges sur pilotis, Premium Plus & emplacements", note: "La grande rangée sud : lodges avec salle d'eau, mobil-homes premium et emplacements." },
  { x: 77, y: 86, name: "Emplacements 416–427", type: "FreeCamp Guinguette", note: "Zone vert anis, l'ambiance guinguette au fond du camping." },
  { x: 42, y: 58, name: "Piscine", type: "Couverte & chauffée", note: "Toboggans 3 pistes et pataugeoires, ouverte toute la saison.", poi: true },
  { x: 66, y: 66, name: "Accueil", type: "Réception du camping", note: "Pain et viennoiseries le matin, conseils et documentation.", poi: true },
  { x: 28, y: 13, name: "Mini-ferme", type: "Les animaux du camping", note: "Le nourrissage avec Dino, le rendez-vous des enfants.", poi: true },
];

export default function PlanPage() {
  const [zoom, setZoom] = useState(1);
  const [zone, setZone] = useState(null);

  return (
    <main data-testid="page-plan" className="grain">
      <section className="relative pt-40 pb-14 px-6 sm:px-10 lg:px-16 bg-cream overflow-hidden">
        <Map className="absolute -right-8 top-20 w-64 h-64 text-pine/5 rotate-12" />
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <span className="font-body text-xs font-bold tracking-[0.25em] uppercase text-terra">
              S'y retrouver en un coup d'œil
            </span>
          </Reveal>
          <Reveal delay={0.1}>
            <h1 className="mt-5 font-display font-black text-4xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tight text-ink max-w-4xl">
              Le <span className="text-pine">plan</span> du camping
            </h1>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-8 max-w-2xl text-ink/65 text-base lg:text-lg leading-relaxed">
              Tous nos emplacements numérotés, l'accueil, la piscine, les sanitaires, la mini-ferme
              et les parkings : repérez votre hébergement avant même d'arriver. Zoomez pour voir
              les numéros d'emplacements.
            </p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-8 flex flex-wrap gap-2" data-testid="plan-amenities">
              {AMENITIES.map((a) => (
                <span key={a.label} className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-xs font-bold bg-sand border border-ink/10 text-ink/60">
                  <a.icon size={14} className="text-pine" /> {a.label}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      <section className="px-6 sm:px-10 lg:px-16 py-16 lg:py-20 bg-cream">
        <div className="max-w-7xl mx-auto">
          <Reveal>
            <div className="relative rounded-[2rem] bg-sand border border-ink/10 shadow-xl shadow-ink/10 overflow-hidden" data-testid="plan-viewer">
              <div className="flex flex-wrap items-center justify-between gap-3 px-5 sm:px-7 py-4 border-b border-ink/10">
                <div className="text-sm font-semibold text-ink/60">
                  Plan du Camping Le Lac aux Oiseaux — Martiel
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setZoom((z) => Math.max(1, +(z - 0.5).toFixed(1)))}
                    disabled={zoom <= 1}
                    data-testid="plan-zoom-out"
                    className="grid place-items-center w-10 h-10 rounded-full border border-ink/10 text-ink/70 transition-colors hover:bg-cream disabled:opacity-40"
                    aria-label="Zoom arrière"
                  >
                    <ZoomOut size={17} />
                  </button>
                  <span className="text-sm font-bold text-pine w-14 text-center" data-testid="plan-zoom-level">{Math.round(zoom * 100)} %</span>
                  <button
                    onClick={() => setZoom((z) => Math.min(3, +(z + 0.5).toFixed(1)))}
                    disabled={zoom >= 3}
                    data-testid="plan-zoom-in"
                    className="grid place-items-center w-10 h-10 rounded-full border border-ink/10 text-ink/70 transition-colors hover:bg-cream disabled:opacity-40"
                    aria-label="Zoom avant"
                  >
                    <ZoomIn size={17} />
                  </button>
                  <button
                    onClick={() => setZoom(1)}
                    data-testid="plan-zoom-reset"
                    className="grid place-items-center w-10 h-10 rounded-full border border-ink/10 text-ink/70 transition-colors hover:bg-cream"
                    aria-label="Réinitialiser le zoom"
                  >
                    <RotateCcw size={16} />
                  </button>
                  <a
                    href="/images/plan-camping.webp"
                    download="plan-camping-lac-aux-oiseaux.webp"
                    data-testid="plan-download"
                    className="inline-flex items-center gap-2 rounded-full bg-pine text-sand text-sm font-semibold px-5 py-2.5 transition-colors hover:bg-pinedark"
                  >
                    <Download size={15} /> Télécharger
                  </a>
                </div>
              </div>
              <div className="overflow-auto max-h-[75vh] bg-white">
                <div className="relative transition-[width] duration-300" style={{ width: `${zoom * 100}%` }}>
                  <img
                    src="/images/plan-camping.webp"
                    alt="Plan du camping Le Lac aux Oiseaux avec les emplacements numérotés, la piscine, les sanitaires et la mini-ferme"
                    className="w-full max-w-none select-none"
                    draggable="false"
                    data-testid="plan-image"
                  />
                  {ZONES.map((z, i) => (
                    <button
                      key={z.name}
                      onClick={() => setZone(zone === i ? null : i)}
                      className="absolute z-20 -translate-x-1/2 -translate-y-1/2 cursor-pointer"
                      style={{ left: `${z.x}%`, top: `${z.y}%` }}
                      data-testid={`plan-zone-${i}`}
                      aria-label={z.name}
                    >
                      <span className="relative grid place-items-center w-6 h-6">
                        <span className={`absolute inset-0 rounded-full animate-ping opacity-30 ${z.poi ? "bg-terra" : "bg-pine"}`} />
                        <span
                          className={`relative w-4 h-4 rounded-full border-2 border-sand shadow-md transition-transform duration-200 hover:scale-125 ${
                            z.poi ? "bg-terra" : "bg-pine"
                          } ${zone === i ? "ring-4 ring-gold scale-125" : ""}`}
                        />
                      </span>
                    </button>
                  ))}
                  <motion.div
                    className="absolute z-10 pointer-events-none"
                    style={{ width: 36, height: 36 }}
                    animate={{
                      left: ["63%", "52%", "40%", "31%", "40%", "52%", "63%"],
                      top: ["66%", "69%", "68%", "57%", "56%", "63%", "66%"],
                    }}
                    transition={{ duration: 46, repeat: Infinity, ease: "linear" }}
                    data-testid="plan-walker"
                  >
                    <motion.span
                      animate={{ y: [0, -4, 0] }}
                      transition={{ duration: 0.55, repeat: Infinity, ease: "easeInOut" }}
                      className="grid place-items-center w-9 h-9 rounded-full bg-sand/95 border-2 border-pine/40 shadow-lg"
                    >
                      <PersonStanding size={17} className="text-pine" />
                    </motion.span>
                  </motion.div>
                  <motion.div
                    className="absolute z-10 pointer-events-none"
                    style={{ width: 30, height: 30 }}
                    animate={{
                      left: ["70%", "58%", "46%", "34%", "46%", "58%", "70%"],
                      top: ["70%", "72%", "70%", "64%", "67%", "71%", "70%"],
                    }}
                    transition={{ duration: 30, repeat: Infinity, ease: "linear", delay: 2 }}
                    data-testid="plan-dog"
                  >
                    <motion.span
                      animate={{ y: [0, -3, 0] }}
                      transition={{ duration: 0.4, repeat: Infinity, ease: "easeInOut" }}
                      className="grid place-items-center w-8 h-8 rounded-full bg-sand/95 border-2 border-terra/40 shadow-lg"
                    >
                      <Dog size={15} className="text-terra" />
                    </motion.span>
                  </motion.div>
                  <motion.div
                    className="absolute z-10 pointer-events-none"
                    style={{ width: 32, height: 32 }}
                    animate={{
                      left: ["12%", "35%", "62%", "85%", "62%", "35%", "12%"],
                      top: ["62%", "66%", "66%", "67%", "66%", "64%", "62%"],
                    }}
                    transition={{ duration: 24, repeat: Infinity, ease: "linear", delay: 6 }}
                    data-testid="plan-cyclist"
                  >
                    <motion.span
                      animate={{ y: [0, -2, 0] }}
                      transition={{ duration: 0.5, repeat: Infinity, ease: "easeInOut" }}
                      className="grid place-items-center w-8 h-8 rounded-full bg-sand/95 border-2 border-brun shadow-lg"
                    >
                      <Bike size={15} className="text-brun" />
                    </motion.span>
                  </motion.div>
                  <motion.div
                    className="absolute z-10 pointer-events-none"
                    style={{ width: 28, height: 28 }}
                    animate={{
                      left: ["72%", "60%", "50%", "43%", "50%", "60%", "72%"],
                      top: ["80%", "72%", "66%", "58%", "66%", "72%", "80%"],
                    }}
                    transition={{ duration: 26, repeat: Infinity, ease: "linear", delay: 10 }}
                    data-testid="plan-kids"
                  >
                    <motion.span
                      animate={{ y: [0, -3, 0] }}
                      transition={{ duration: 0.35, repeat: Infinity, ease: "easeInOut" }}
                      className="grid place-items-center w-7 h-7 rounded-full bg-gold/95 border-2 border-sand shadow-lg"
                    >
                      <Baby size={14} className="text-pinedark" />
                    </motion.span>
                  </motion.div>
                </div>
              </div>
              <p className="px-5 py-3 text-center font-hand text-2xl text-terra -rotate-1">
                touchez les pastilles pour identifier chaque zone — et suivez le promeneur, son chien, le cycliste et les enfants !
              </p>
              <AnimatePresence>
                {zone !== null && (
                  <motion.div
                    initial={{ opacity: 0, y: -12 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -12 }}
                    transition={{ duration: 0.3 }}
                    className="absolute left-3 top-16 sm:top-20 z-30 max-w-[280px] sm:max-w-xs rounded-2xl bg-sand/95 backdrop-blur border border-pine/25 shadow-2xl p-5"
                    data-testid="plan-zone-card"
                  >
                    <button
                      onClick={() => setZone(null)}
                      data-testid="plan-zone-close"
                      className="absolute top-3 right-3 grid place-items-center w-7 h-7 rounded-full bg-ink/5 text-ink/60 hover:bg-ink/10"
                      aria-label="Fermer"
                    >
                      <X size={14} />
                    </button>
                    <div className="text-[10px] font-bold tracking-[0.2em] uppercase text-terra">
                      {ZONES[zone].name}
                    </div>
                    <div className="mt-1 font-display font-bold text-xl text-ink pr-6">{ZONES[zone].type}</div>
                    <p className="mt-2 text-sm text-ink/60 leading-relaxed">{ZONES[zone].note}</p>
                    <Link
                      to="/hebergements"
                      data-testid="plan-zone-link"
                      className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-pine hover:text-terra transition-colors"
                    >
                      Voir les hébergements <ArrowUpRight size={14} />
                    </Link>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="mt-8 rounded-2xl bg-brun/20 border border-brun/40 p-5 flex items-start gap-4" data-testid="plan-info">
              <Info size={20} className="text-terra shrink-0 mt-0.5" />
              <p className="text-sm text-ink/70 leading-relaxed">
                Chaque couleur du plan correspond à un type d'hébergement (voir la légende en bas du plan) :
                mobil-homes, lodges sur pilotis, Freeflower ou emplacements nus. Pour la tranquillité de tous,
                la circulation des véhicules est interdite entre 23h et 7h dans le camping.
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.25}>
            <a
              href={BOOKING_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="plan-cta-booking"
              className="mt-10 flex items-center justify-between gap-4 rounded-[1.75rem] bg-pine text-sand p-6 sm:p-8 transition-all duration-300 hover:bg-pinedark hover:-translate-y-1 hover:shadow-xl hover:shadow-pine/25"
            >
              <div>
                <div className="font-display font-bold text-2xl sm:text-3xl">Vous avez repéré votre coin préféré ?</div>
                <p className="mt-1.5 text-sm sm:text-base text-sand/70">
                  Indiquez le numéro d'emplacement souhaité dans votre demande, on fera le maximum.
                </p>
              </div>
              <span className="grid place-items-center w-12 h-12 shrink-0 rounded-full bg-gold text-pinedark">
                <ArrowUpRight size={20} />
              </span>
            </a>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
